/**
 * The Watchlist's heatmap (roadmap D117), after MojoPSA's: the template's
 * matrix of likelihood (rows, most likely on top) by impact (columns), each
 * cell in its band's colour with the number of open risks placed in it, on
 * the residual or the inherent assessment and on one dimension. Pressing a
 * cell filters the list below to the risks in it. Beside it: the thresholds
 * (the bands and their score ranges) and the appetite at each level; under
 * it, the two scales and what each step means.
 *
 * Colour is never the only signal: every cell says its score and count, and
 * names its band in its label.
 */
import './risks.css';

import { Label, ListBox, Select } from '@heroui/react';
import type { ReactNode } from 'react';

import { APPETITE_LEVEL_TEXT, type AppetiteLevel, appetiteOf, bandById, dimensionsOf, type HeatCell, heatmap } from '@aioe/shared/risks/standard';
import type { RiskEntry, RiskSettings, RiskTemplate } from '@aioe/shared';
import { ArtSection } from '../ui/Art.tsx';
import { cellWords, inkOn } from './watchlistModel.ts';

export interface MatrixView {
  which: 'inherent' | 'residual';
  dimension: string;
}

export function RiskMatrix({
  entries,
  template,
  settings,
  view,
  onView,
  selected,
  onCell,
  tone,
  icon,
}: {
  entries: readonly RiskEntry[];
  template: RiskTemplate;
  settings: RiskSettings | undefined;
  view: MatrixView;
  onView: (next: MatrixView) => void;
  selected?: { likelihood: number; impact: number };
  /** A cell pressed (with its words, "Likely × Major"), or pressed again to clear it. */
  onCell: (cell: HeatCell | undefined, label?: string) => void;
  tone: Parameters<typeof ArtSection>[0]['tone'];
  icon: ReactNode;
}) {
  const dims = dimensionsOf(template);
  const dim = dims.find((d) => d.id === view.dimension) ?? dims[0];
  const map = heatmap(entries, template, { which: view.which, dimension: dim.id, settings: settings ?? {} });
  const placed = map.rows.reduce((n, r) => n + r.reduce((m, c) => m + c.uids.length, 0), 0);
  const impactSteps = [...dim.scale].sort((a, b) => a.level - b.level);
  return (
    <ArtSection
      tone={tone}
      icon={icon}
      title="Risk matrix"
      count={placed}
      hint={`${view.which === 'residual' ? 'Residual' : 'Inherent'} · ${dim.label}`}
      actions={
        <span className="flex flex-wrap items-end gap-2">
          <Select className="w-32" variant="secondary" selectedKey={view.which} onSelectionChange={(k) => k !== null && onView({ ...view, which: String(k) as MatrixView['which'] })} data-risk-matrix-assessment>
            <Label className="sr-only">Assessment</Label>
            <Select.Trigger className="min-h-8 py-1 text-xs" aria-label="Assessment">
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                <ListBox.Item id="residual" textValue="Residual">
                  Residual
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item id="inherent" textValue="Inherent">
                  Inherent
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              </ListBox>
            </Select.Popover>
          </Select>
          <Select className="w-36" variant="secondary" selectedKey={dim.id} onSelectionChange={(k) => k !== null && onView({ ...view, dimension: String(k) })} data-risk-matrix-dimension>
            <Label className="sr-only">Dimension</Label>
            <Select.Trigger className="min-h-8 py-1 text-xs" aria-label="Dimension">
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {dims.map((d) => (
                  <ListBox.Item key={d.id} id={d.id} textValue={d.label}>
                    {d.label}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        </span>
      }
    >
      <div className="risk-matrix" data-risk-matrix style={{ ['--cols' as string]: String(impactSteps.length) }}>
        <span className="risk-matrix__axis risk-matrix__axis--y" aria-hidden="true">
          Likelihood
        </span>
        <div className="risk-matrix__grid" role="grid" aria-label={`Risk matrix, ${view.which}, ${dim.label}`}>
          {map.rows.map((row) => (
            <div key={row[0]?.likelihood} className="contents" role="row">
              <span className="risk-matrix__label risk-matrix__label--row" role="rowheader">
                {template.likelihood.find((s) => s.level === row[0]?.likelihood)?.label}
              </span>
              {row.map((cell) => {
                const on = selected?.likelihood === cell.likelihood && selected?.impact === cell.impact;
                const words = cellWords(cell, template, dim.scale);
                return (
                  <button
                    key={`${cell.likelihood}:${cell.impact}`}
                    type="button"
                    role="gridcell"
                    className="risk-cell"
                    style={{ ['--band-colour' as string]: cell.band?.colour ?? 'var(--muted)', ['--band-ink' as string]: cell.band ? inkOn(cell.band.colour) : undefined }}
                    data-count={cell.uids.length}
                    data-selected={on || undefined}
                    aria-pressed={on}
                    aria-label={words}
                    title={words}
                    disabled={cell.uids.length === 0 && !on}
                    onClick={() => onCell(on ? undefined : cell, `${template.likelihood.find((s) => s.level === cell.likelihood)?.label ?? cell.likelihood} × ${dim.scale.find((s) => s.level === cell.impact)?.label ?? cell.impact}`)}
                    data-risk-cell={`${cell.likelihood}x${cell.impact}`}
                  >
                    <span className="risk-cell__count">{cell.uids.length || ''}</span>
                    <span className="risk-cell__score">{cell.score}</span>
                  </button>
                );
              })}
            </div>
          ))}
          <span aria-hidden="true" />
          {impactSteps.map((s) => (
            <span key={s.level} className="risk-matrix__label risk-matrix__label--col" role="columnheader">
              {s.label}
            </span>
          ))}
        </div>
        <span className="risk-matrix__axis risk-matrix__axis--x" aria-hidden="true">
          Impact{dim.id === 'overall' ? '' : ` — ${dim.label}`}
        </span>
      </div>
      {map.unplaced || map.otherTemplate ? (
        <p className="m-0 mt-2 text-xs text-muted" data-risk-unplaced>
          {[map.unplaced ? `${map.unplaced} open risk${map.unplaced === 1 ? ' is' : 's are'} not rated${dim.id === 'overall' ? '' : ` on ${dim.label}`} yet` : '', map.otherTemplate ? `${map.otherTemplate} on another template` : ''].filter(Boolean).join('; ')}.
        </p>
      ) : null}
    </ArtSection>
  );
}

/** The bands and their score ranges, lowest first, as MojoPSA lists its thresholds. */
export function ThresholdsPanel({ template }: { template: RiskTemplate }) {
  return (
    <section className="risk-panel" aria-label="Thresholds" data-risk-thresholds>
      <span className="pane-label">Thresholds</span>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {[...template.bands]
          .sort((a, b) => a.min - b.min)
          .map((b) => (
            <li key={b.id} className="risk-panel__row">
              <span className="risk-band" style={{ ['--band-colour' as string]: b.colour }}>
                <span className="risk-band__swatch" aria-hidden="true" />
                {b.label}
              </span>
              <span className="tabular-nums text-muted">
                {b.min}–{b.max}
              </span>
            </li>
          ))}
      </ul>
    </section>
  );
}

/** The most each level accepts: the organisation, the project, and (on a repository's view) this repository. */
export function AppetitePanel({ template, settings, rootId, onEdit }: { template: RiskTemplate; settings: RiskSettings | undefined; rootId: string; onEdit?: () => void }) {
  const a = appetiteOf(settings, template, rootId);
  const levels: AppetiteLevel[] = rootId ? ['organisation', 'project', 'repository'] : ['organisation', 'project'];
  return (
    <section className="risk-panel" aria-label="Risk appetite" data-risk-appetite>
      <span className="flex items-center justify-between gap-2">
        <span className="pane-label">Risk appetite</span>
        {onEdit ? (
          <button type="button" className="risk-panel__edit" onClick={onEdit}>
            Change
          </button>
        ) : null}
      </span>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {levels.map((level) => {
          const band = bandById(a[level], template);
          return (
            <li key={level} className="risk-panel__row">
              <span className="text-foreground">{APPETITE_LEVEL_TEXT[level]}</span>
              <span className="flex items-center gap-1.5 text-muted">
                max
                {band ? (
                  <span className="risk-band" style={{ ['--band-colour' as string]: band.colour }}>
                    <span className="risk-band__swatch" aria-hidden="true" />
                    {band.label}
                  </span>
                ) : (
                  '—'
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The two scales and what each step means; the impact scale is the chosen dimension's. */
export function ScalesSection({ template, dimension, tone, icon }: { template: RiskTemplate; dimension: string; tone: Parameters<typeof ArtSection>[0]['tone']; icon: ReactNode }) {
  const dim = dimensionsOf(template).find((d) => d.id === dimension) ?? dimensionsOf(template)[0];
  return (
    <ArtSection tone={tone} icon={icon} title="Scales" hint={template.name}>
      <div className="grid gap-4 @3xl:grid-cols-2" data-risk-scales>
        {[
          { title: 'Likelihood', steps: template.likelihood },
          { title: dim.id === 'overall' ? 'Impact' : `Impact — ${dim.label}`, steps: dim.scale },
        ].map((s) => (
          <div key={s.title} className="flex min-w-0 flex-col gap-1.5">
            <span className="pane-label">{s.title}</span>
            <ol className="m-0 flex list-none flex-col gap-1 p-0">
              {[...s.steps]
                .sort((a, b) => b.level - a.level)
                .map((step) => (
                  <li key={step.level} className="risk-scale-row">
                    <span className="risk-scale-row__level tabular-nums">{step.level}</span>
                    <span className="min-w-0">
                      <span className="text-sm font-medium text-foreground">{step.label}</span>
                      {step.description ? <span className="block text-xs text-muted">{step.description}</span> : null}
                    </span>
                  </li>
                ))}
            </ol>
          </div>
        ))}
      </div>
    </ArtSection>
  );
}
